import { applyAliases, normalizePedalName, enhancedPedalMatch } from './pedalDatabase';

/**
 * Want IDENTITY — "is this the same pedal I already asked for?"
 *
 * Deliberately separate from the matching predicates in matchingLogic.js, which
 * answer a different question ("would this listing interest me?"). Conflating
 * the two is what let duplicates through: doesPedalMatchWant requires effect
 * types to overlap, so two wants for the same pedal tagged with different
 * effects looked like unrelated wants to the guard on the listing page.
 *
 * Identity is make + model and nothing else — not effect types, price,
 * condition, decade, colour or excluded keywords. Those are all preferences
 * about the same pedal, and a second want is the wrong way to express them.
 */

const text = (value) => (typeof value === 'string' ? value.trim() : '');

/**
 * Comparable key for a want, or null when it names no pedal at all.
 *
 * Aliases are resolved first so "EHX" and "Electro-Harmonix" agree, then the
 * result is normalized. Both steps are needed: applyAliases returns the alias
 * table's own spelling, so a name that hits the table and a variant that misses
 * it can come out differing only by case — which is how "Zvex" and "ZVEX" both
 * ended up on one Wantlist.
 *
 * A want naming neither make nor model (an effect-type-only want) has no
 * identity under this rule and returns null, so callers never treat two
 * unrelated ones — say "any delay" and "any tuner" — as the same want.
 */
export const wantIdentityKey = (want) => {
  if (!want) return null;

  const make = text(want.make);
  const model = text(want.model);
  if (!make && !model) return null;

  const resolved = applyAliases(make, model);
  return `${normalizePedalName(resolved.make)}|${normalizePedalName(resolved.model)}`;
};

/**
 * The first want in `wants` that is the same pedal as `candidate`, or null.
 *
 * Pass the id of the want being edited as `excludeId` so a want never counts as
 * its own duplicate — that is what lets the check stay on during an edit rather
 * than being switched off, which previously left the edit path unguarded.
 */
export const findDuplicateWant = (wants, candidate, { excludeId = null } = {}) => {
  const key = wantIdentityKey(candidate);
  if (!key || !Array.isArray(wants)) return null;

  return wants.find(
    (want) => want && want.id !== excludeId && wantIdentityKey(want) === key
  ) || null;
};

/**
 * Whether a want and a listing name the same pedal under the app's fuzzy
 * matcher — "Boss SD-1" and "Boss SD-1 Super Overdrive", or "Strymon Timeline"
 * and "Strymon Timeline Delay".
 *
 * This is looser than wantIdentityKey on purpose, and is only for ADVISORY use
 * (the listing page's conflict modal). It is deliberately NOT used to hard-block
 * a save: the same fuzziness that unifies "SD-1" with "SD-1 Super Overdrive" also
 * unifies "ProCo Rat" with "ProCo Lil Rat", which are different pedals. That is
 * why the modal offers to add the pedal anyway when this is what matched, and
 * does not when the exact identity rule matched.
 *
 * Both sides must name a specific pedal. That restriction is the whole reason
 * this is a separate function rather than a call to doesPedalMatchWant: that
 * predicate skips the model comparison whenever the want has no model, so a
 * brand-only "Boss" want registered as the same pedal as every Boss listing,
 * and an effect-type-only want registered as the same pedal as anything sharing
 * its effect. Both are correct for matching and wrong for this question.
 */
export const isSameNamedPedal = (want, pedal) => {
  if (!want || !pedal) return false;

  const wantMake = text(want.make);
  const wantModel = text(want.model);
  const pedalMake = text(pedal.make);
  const pedalModel = text(pedal.model);
  if (!wantMake || !wantModel || !pedalMake || !pedalModel) return false;

  return enhancedPedalMatch(
    { make: wantMake, model: wantModel },
    { make: pedalMake, model: pedalModel }
  );
};

/**
 * The duplicate that should actually block a save, or null.
 *
 * Differs from findDuplicateWant in one way that matters: when editing, a
 * collision only blocks if this edit is what creates it. Some accounts already
 * hold duplicate pairs from before these guards existed, and refusing to save
 * either copy would trap the owner — they cannot fix a want they cannot save.
 * So an edit that leaves make and model alone is always allowed through, and
 * only an edit that retargets a want onto another want's pedal is blocked.
 */
export const findBlockingDuplicate = (wants, candidate, { editingWant = null } = {}) => {
  if (editingWant) {
    const originalKey = wantIdentityKey(editingWant);
    const candidateKey = wantIdentityKey(candidate);
    if (originalKey && originalKey === candidateKey) return null;
  }

  return findDuplicateWant(wants, candidate, { excludeId: editingWant?.id || null });
};
